import { createBrowserRouter, type RouteObject } from 'react-router';

import { AppLayout } from '@/app/layouts/AppLayout';
import { AuthLayout } from '@/app/layouts/AuthLayout';
import { ROUTES } from '@/constants/routes';
import { attendanceRoutes } from '@/features/attendance';
import { accountRoutes, guestRoutes, HomeRedirect, RequireAuth } from '@/features/auth';
import { billingRoutes } from '@/features/billing';
import { dashboardRoutes } from '@/features/dashboard';
import { memberRoutes } from '@/features/members';
import { packageRoutes } from '@/features/packages';
import { prospectRoutes } from '@/features/prospects';
import { settingsRoutes } from '@/features/settings';
import { staffRoutes } from '@/features/staff';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { RouteErrorPage } from '@/pages/RouteErrorPage';

/**
 * Features contribute their own `RouteObject[]` (with `lazy` for code-splitting) and get spread
 * in here — adding a screen never means editing a layout. Each feature wraps its screens in
 * `RequirePermission`.
 */
const featureRoutes: RouteObject[] = [
  ...dashboardRoutes,
  ...memberRoutes,
  ...billingRoutes,
  ...attendanceRoutes,
  ...prospectRoutes,
  ...packageRoutes,
  ...staffRoutes,
  ...settingsRoutes,
];

/**
 * Signed out → the login page. Signed in → `RequireAuth` loads the profile and staff get the app
 * shell. The website is for staff only: member logins are turned away there, because members use
 * the mobile app. A pending forced password change holds everyone on the change-password screen.
 */
const routes: RouteObject[] = [
  {
    // The app-wide error boundary.
    errorElement: <RouteErrorPage />,
    children: [
      { element: <AuthLayout />, children: guestRoutes },
      {
        element: <RequireAuth />,
        children: [
          { path: ROUTES.HOME, element: <HomeRedirect /> },
          { element: <AuthLayout />, children: accountRoutes },
          {
            element: <AppLayout />,
            // A failing screen shows its error inside the shell, so the menu still works.
            children: [{ errorElement: <RouteErrorPage />, children: featureRoutes }],
          },
        ],
      },
      { path: ROUTES.NOT_FOUND, element: <NotFoundPage /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
